/**
 * Patch 37.4.43 · Persistent AI Studio Pack detail route.
 *
 * Route: /ai-studio/packs/:packId
 *
 * Solves 37.4.42 iter-78 finding #4 — "the generated social_only pack
 * and its bulk-review action are no longer reachable from AI Studio
 * after navigation/refresh". Before this patch the wizard's
 * ``activePack`` lived in ``useState`` on ``AIStudioPage``, so a
 * refresh or navigation-away wiped access to Send-all-to-review and
 * the assets grid. The founder had to either (a) remember to keep
 * the ``?pack=<id>`` query string in the URL, or (b) hunt through
 * Content Ops (a different surface with different UX).
 *
 * This page:
 *   • Hydrates the pack + assets from the server on mount (survives
 *     refresh, deep link, and back-button navigation).
 *   • Renders the exact same ``PackPanel`` used inline on
 *     ``AIStudioPage`` — Send-all-to-review, live counts, per-asset
 *     regenerate/send. No UI logic duplicated.
 *   • Surfaces a ``Review Session →`` CTA when any head is
 *     ``needs_review``, routing to ``/campaigns/{packId}/review-session``
 *     — the same URL Content Ops uses so the queue query matches
 *     byte-for-byte.
 *   • Provides a ``← Back to AI Studio`` breadcrumb so the founder
 *     never feels stranded on the detail route.
 *
 * No storage-model merge. No production data mutated.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Icons from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  fetchPack,
  fetchPackAssets,
  regenerateAsset,
  sendAssetToReview,
  sendPackToReview,
  type BulkSendToReviewOut,
} from "@/entities/ai-studio";
import { useCurrentWorkspace } from "@/entities/workspace";
import { useUpgradePrompt } from "@/features/entitlements/useEntitlements";
import { PackPanel } from "@/pages/ai-studio/AIStudioPage";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Skeleton } from "@/shared/ui/skeleton";

export function AIStudioPackDetailPage(): JSX.Element {
  const { packId = "" } = useParams<{ packId: string }>();
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const upgrade = useUpgradePrompt();

  const packQ = useQuery({
    queryKey: ["campaign-pack", ws?.id, packId],
    queryFn: () => fetchPack(ws!.id, packId),
    enabled: !!ws && !!packId,
    // Poll while the pack is still generating so live counts + assets
    // catch up without the founder needing to refresh.
    refetchInterval: (q) =>
      q.state.data && q.state.data.status === "generating" ? 3000 : false,
  });
  const assetsQ = useQuery({
    queryKey: ["campaign-pack-assets", packId],
    queryFn: () => fetchPackAssets(ws!.id, packId),
    enabled: !!ws && !!packId,
    refetchInterval: () => {
      // Same 3s cadence as the pack query while generating so the
      // assets grid stays in lock-step with the progress bar.
      const p = qc.getQueryData<{ status?: string }>(["campaign-pack", ws?.id, packId]);
      return p?.status === "generating" ? 3000 : false;
    },
  });

  const regen = useMutation({
    mutationFn: (assetId: string) => regenerateAsset(ws!.id, assetId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["campaign-pack-assets", packId] });
      toast.success("Regeneration queued");
    },
    onError: (e: unknown) => {
      const err = e as { status?: number; code?: string; message?: string; details?: Record<string, unknown> };
      if (err.status === 402 || err.code === "text_generation_allowance_exhausted") {
        upgrade.open({
          title: "Monthly text capacity reached",
          reason: err.message || "Upgrade for unlimited text generation or wait for the monthly reset.",
          upgrade_target: err.details?.upgrade_target as string | undefined,
        });
        return;
      }
      toast.error(err.message || "Regenerate failed");
    },
  });
  const sendReview = useMutation({
    mutationFn: (assetId: string) => sendAssetToReview(ws!.id, assetId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["campaign-pack-assets", packId] });
      toast.success("Sent to review");
    },
  });
  const sendPack = useMutation({
    mutationFn: () => sendPackToReview(ws!.id, packId),
    onSuccess: (r: BulkSendToReviewOut) => {
      void qc.invalidateQueries({ queryKey: ["campaign-pack-assets", packId] });
      void qc.invalidateQueries({ queryKey: ["campaign-pack", ws?.id, packId] });
      const parts: string[] = [];
      if (r.sent > 0) parts.push(`${r.sent} sent`);
      if (r.skipped_not_draft > 0) parts.push(`${r.skipped_not_draft} already in review`);
      if (r.skipped_locked > 0) parts.push(`${r.skipped_locked} locked`);
      // Patch 37.4.44 · surface the typed blocked outcome. The founder
      // sees "1 needs recovery" in the toast instead of the bulk
      // operation silently completing. The tone downgrades to a
      // warning when nothing was actually sent.
      const needsRecovery = r.needs_recovery ?? 0;
      if (needsRecovery > 0) parts.push(`${needsRecovery} need recovery`);
      if (r.errors > 0) parts.push(`${r.errors} failed`);
      const summary = parts.length ? parts.join(" · ") : "Nothing to send";
      if (r.errors > 0) {
        toast.error(`Send to review — ${summary}`);
      } else if (r.sent === 0 && (needsRecovery > 0 || r.skipped_locked > 0)) {
        toast.warning(`Send to review — ${summary}`);
      } else {
        toast.success(`Send to review — ${summary}`);
      }
    },
    onError: (e: unknown) => toast.error((e as Error).message || "Bulk send failed"),
  });

  if (!ws) {
    return <Skeleton className="h-64 w-full" />;
  }
  if (packQ.isLoading) {
    return (
      <div className="mx-auto max-w-5xl space-y-4 p-4" data-testid="ai-studio-pack-detail-loading">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (packQ.isError || !packQ.data) {
    return (
      <Card
        data-testid={`ai-studio-pack-detail-not-found-${packId}`}
        className="mx-auto mt-8 max-w-lg border-border/70 bg-card/70 backdrop-blur"
      >
        <CardHeader>
          <CardTitle className="font-display text-lg">Pack not found</CardTitle>
          <CardDescription>
            The pack you&apos;re looking for was archived, doesn&apos;t exist in this
            workspace, or belongs to another tenant.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            data-testid={`ai-studio-pack-detail-back-${packId}`}
            variant="outline"
            onClick={() => navigate("/ai-studio")}
          >
            ← Back to AI Studio
          </Button>
        </CardContent>
      </Card>
    );
  }

  const pack = packQ.data;
  const assets = assetsQ.data ?? [];
  const needsReviewCount = pack.live_by_status?.needs_review ?? 0;

  return (
    <div
      className="mx-auto max-w-5xl space-y-4 p-4"
      data-testid={`ai-studio-pack-detail-${packId}`}
    >
      <header className="flex flex-wrap items-center gap-3">
        <Link
          to="/ai-studio"
          data-testid={`ai-studio-pack-detail-back-${packId}`}
          className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground"
        >
          <Icons.ChevronLeft className="h-3 w-3" /> Back to AI Studio
        </Link>
        <span
          data-testid={`ai-studio-pack-origin-${packId}`}
          className="rounded-full border border-border/70 px-2 py-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground"
        >
          {pack.pack_origin === "campaign" ? "Campaign generation" : "Standalone Studio generation"}
        </span>
        {pack.campaign_room_path ? (
          <Link
            to={pack.campaign_room_path}
            data-testid={`ai-studio-pack-open-campaign-room-${packId}`}
            className="inline-flex items-center gap-2 rounded-md border border-amber-400/40 bg-amber-400/10 px-3 py-1.5 text-xs font-medium text-amber-100 hover:bg-amber-400/20"
          >
            Open Campaign Room →
          </Link>
        ) : null}
        {needsReviewCount > 0 ? (
          <Link
            to={`/campaigns/${packId}/review-session`}
            data-testid={`ai-studio-pack-open-review-session-${packId}`}
            className="ml-auto inline-flex items-center gap-2 rounded-md border border-cyan-400/40 bg-cyan-400/10 px-3 py-1.5 text-xs font-medium text-cyan-100 hover:bg-cyan-400/20"
          >
            Review Session ({needsReviewCount}) →
          </Link>
        ) : null}
      </header>
      <PackPanel
        pack={pack}
        assets={assets}
        onRegenerate={(id) => regen.mutate(id)}
        onSendReview={(id) => sendReview.mutate(id)}
        regenPending={regen.isPending}
        onSendPack={() => sendPack.mutate()}
        sendPackPending={sendPack.isPending}
      />
    </div>
  );
}

export default AIStudioPackDetailPage;
